// MobileTabBar — 모바일 하단 고정 탭바(주력 앱 + 메뉴). 엄지 닿는 하단, safe-area 대응.
// 갓파일이 아님. A 배선: 모바일일 때 앱 루트에 <MobileTabBar onMenuClick={()=>setDrawerOpen(true)} /> 렌더.
// 가시성은 menuAccess(canSeeMenu) 단일 원칙 — 접근 권한 없는 앱은 자동 제외(메뉴 버튼으로 전체 nav 접근).
import { useNavigate, useLocation } from 'react-router-dom';
import { IconMessages, IconFolder, IconSparkles, IconMicrophone, IconMenu2 } from '@tabler/icons-react';
import useVisualViewport from '../hooks/useVisualViewport';
import { canSeeMenu } from '../utils/menuAccess';
import { requestEmbedReload } from '../utils/embedReload';
import { menuLabel, readCachedConfig } from '../utils/sidebarMenu';
import { tabbarCalc } from '../lib/tabbarMetrics';

// 주력 4종(2026-06-21 메신저·드라이브·AI채팅 + 2026-08-21 AI노트 — 요청: 지윤) —
// 접근 권한 있는 것만 노출(canSeeMenu 필터, 권한 없으면 자동 제외=에러 없음).
// 제외분/나머지는 '메뉴'(드로어)에서 전체 nav 접근.
// 폭: flex:1 균등분할이라 탭 수가 늘면 칸이 좁아진다. 5탭 실측(2026-08-21) — 360px 이상은 라벨 안 잘리고,
// 320px 에서만 '드라이브'(글자 38px > 칸 35px)가 말줄임된다. 기능엔 영향 없어 그대로 둔다.
const PRIMARY = [
  { key: 'messenger', label: '메신저',   path: '/messenger', Icon: IconMessages },
  { key: 'drive',     label: '드라이브', path: '/drive-app', Icon: IconFolder },
  { key: 'chat',      label: 'AI 채팅',  path: '/',          Icon: IconSparkles },   // 시안 F: AI 채팅 = 반짝이(사이드바 메뉴 아이콘과 같다 · f8b2)
  { key: 'meetings',  label: 'AI 노트',  path: '/meetings',  Icon: IconMicrophone },
];

// 같은 탭을 다시 누를 때 그 앱의 iframe 만 새로고침할 대상 — **메신저 전용**.
// (MM 모바일 메뉴가 바깥 탭 이동으로는 닫히지 않아 생기는 '먹통' 체감을 푸는 우회책.
//  다른 탭에는 키를 추가하지 않는다 — 드라이브·AI 채팅·AI 노트 재탭 동작 회귀가 된다.)
const RETAP_RELOAD = { messenger: 'messenger' };

// 색은 전부 --gw-* 토큰(라이트 기본값 = 폴백, public/index.html 정의 · 다크는 토큰이 바뀐다) — f8b2.
//   선택 칸 = 연한 파랑 바탕 + 위 2px 파랑 줄(TAB_MARK) + 진한 글자 · 비선택 글자 = --gw-secondary.
const TAB_MARK = { position: 'absolute', top: 0, left: '22%', right: '22%', height: '2px', borderRadius: '0 0 2px 2px', background: 'var(--gw-blue, #2383e2)' };
const tabBtn = (active) => ({
  flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px',
  padding: '8px 4px 6px', minHeight: '56px', border: 'none', background: active ? 'var(--gw-blue-soft, #eaf3fc)' : 'transparent',
  cursor: 'pointer', color: active ? 'var(--gw-text, #32312e)' : 'var(--gw-secondary, #64635f)',
  WebkitTapHighlightColor: 'transparent',
});

export default function MobileTabBar({ onMenuClick }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { keyboardOpen } = useVisualViewport();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const tabs = PRIMARY.filter((t) => canSeeMenu(user, t.key));
  // 라벨은 관리자 메뉴 설정(사이드바 이름)을 따른다. 탭 구성·순서는 그대로 — 숨긴 메뉴도 탭엔 남는다.
  const menuConfig = readCachedConfig();
  const isActive = (path) => (path === '/' ? pathname === '/' || pathname === '/chat' : pathname.startsWith(path));

  return (
    <>
    {/* 탭바(TABBAR_H(lib/tabbarMetrics)+safe-area)가 페이지 최하단 콘텐츠를 가리지 않게 — 'gw-tabbar-pad' 클래스에 하단 여백.
        이 <style>은 탭바(=모바일)에서만 마운트되므로 데스크톱엔 규칙 자체가 없음(무영향). */}
    {/* g2730 — 임베드(EmbedPage) 래퍼만 'gw-embed-banner-pad' 를 더 달아, 푸시 안내 띠가 떠 있으면 «탭바 몫과 띠 몫 중 큰 쪽»만큼 비운다
        (띠 높이 변수 `--gw-push-banner-clear` 는 탭바 위에 뜬 띠의 바닥 거리까지 이미 포함한다 · 없으면 0px = 이전과 같다).
        선택자가 둘이라 위 일반 규칙보다 구체적이다. 키보드 규칙도 같은 꼴로 한 벌 더 — 순서는 «일반 → 키보드 → 임베드 → 임베드 키보드». */}
    {/* g2829a — 위 4규칙 뒤에 «클래스 단 곳도 띠 몫까지 / 공통 틀 본문 첫 자식 기본값(+키보드 0)» 3규칙을 덧붙였다(기존 4규칙 글자는 그대로).
        ① 클래스를 단 곳이 푸시 안내 띠 몫(`--gw-push-banner-clear`)까지 비운다 — 띠가 없으면 0px 라 이전과 같다.
           단 `:not([role="dialog"])` — 띠보다 위(딤 위)에 뜨는 대화상자 시트(스티커 시트 StickerPicker)는 띠에 안 가리므로 탭바 몫만(옛 첫 규칙 60) — 띠 몫을 주면 시트 안 칸이 줄어든다.
           키보드 0 은 2번 규칙이 특이도로 이긴다(0,2,1 > 0,2,0) — 같은 줄을 뒤에 또 두지 않는다(g2829c2).
        ② `.gw-shell-body` 의 첫 자식은 클래스를 안 달아도 같은 여백을 받는다(새 화면이 클래스를 잊어도 탭바·띠에 안 가린다) — 뒤 형제 fixed 모달은 제외하려고 `>:first-child` 만.
           특이도가 (0,3,0)이라 옛 키보드 규칙(0,2,1)을 이기므로 그 키보드 0 규칙(0,4,1)이 따로 필요하다.
           셸 본문 첫 자식이 여백을 받는다 — 페이지 맨 앞에 조건부 형제를 두지 말 것 · 두려면 그 페이지에 클래스를 남긴다(g2829c1 L3).
        ③ 자체 여백이 있는 화면은 AppShell 에 `ownTabbarPad` 를 넘겨 ②에서 빠진다(관리자·협업도구·채팅·회의록·임베드). */}
    <style>{`.gw-tabbar-pad{padding-bottom:${tabbarCalc()} !important}\nhtml[data-gw-keyboard="1"] .gw-tabbar-pad{padding-bottom:0 !important}\n.gw-tabbar-pad.gw-embed-banner-pad{padding-bottom:max(${tabbarCalc()}, var(--gw-push-banner-clear, 0px)) !important}\nhtml[data-gw-keyboard="1"] .gw-tabbar-pad.gw-embed-banner-pad{padding-bottom:0 !important}\n.gw-tabbar-pad:not([role="dialog"]){padding-bottom:max(${tabbarCalc()}, var(--gw-push-banner-clear, 0px)) !important}\n.gw-shell-body:not([data-gw-own-tabbar-pad])>:first-child{padding-bottom:max(${tabbarCalc()}, var(--gw-push-banner-clear, 0px)) !important}\nhtml[data-gw-keyboard="1"] .gw-shell-body:not([data-gw-own-tabbar-pad])>:first-child{padding-bottom:0 !important}`}</style>
    <nav aria-label="모바일 주요 메뉴" style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1000, display: keyboardOpen ? 'none' : 'flex',
      background: 'var(--gw-bg, #ffffff)', borderTop: '1px solid var(--gw-line, #eeeeec)',   // 그림자 없이 선만(시안 상단 바와 같다)
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {tabs.map((t) => {
        const active = isActive(t.path);
        return (
          <button key={t.key} type="button" aria-current={active ? 'page' : undefined} style={tabBtn(active)}
            onClick={() => {
              // 같은 탭 재탭 = 그 앱 iframe 만 새로고침(상위 SPA·다른 앱 keep-alive 는 그대로).
              // 첫 진입(비활성)은 평소대로 이동한다.
              if (active && RETAP_RELOAD[t.key]) { requestEmbedReload(RETAP_RELOAD[t.key]); return; }
              navigate(t.path);
            }}>
            {active && <span aria-hidden="true" style={TAB_MARK} />}
            <t.Icon size={23} stroke={1.65} />
            <span style={{ width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: active ? 700 : 500 }}>{menuLabel(t.key, menuConfig)}</span>
          </button>
        );
      })}
      {(() => {
        const menuActive = !tabs.some((t) => isActive(t.path));
        return (
          <button type="button" onClick={onMenuClick} title="메뉴" aria-label="전체 메뉴 열기" aria-current={menuActive ? 'page' : undefined} style={tabBtn(menuActive)}>
            {menuActive && <span aria-hidden="true" style={TAB_MARK} />}
            <IconMenu2 size={23} stroke={1.65} />
            <span style={{ width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: menuActive ? 700 : 500 }}>메뉴</span>
          </button>
        );
      })()}
    </nav>
    </>
  );
}
